import React, { useEffect, useState } from 'react';
import { generatePDF } from './generatePDF';
import { fontStyles, labelColors, valueColors, lineColors } from './utils/fontStyles';
import { groupByEColumn } from './utils/helpers';
import * as XLSX from 'xlsx';
//import './App.css'; // ✅ use correct case

function SheetToPDF() {
  const [pageSize, setPageSize] = useState('acustom');
  const [orientation, setOrientation] = useState('landscape');
  const [mode, setMode] = useState('all');
  const [sheet1Data, setSheet1Data] = useState([]);
  const [sheet2Data, setSheet2Data] = useState([]);
  const [gridCounts, setGridCounts] = useState({});
  const [totalGrids, setTotalGrids] = useState(0);
  const [classLabel, setClassLabel] = useState('Class');
  const [excelFileName, setExcelFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isReadingExcel, setIsReadingExcel] = useState(false);
  const [timeTaken, setTimeTaken] = useState(null);
  const [borderOptions, setBorderOptions] = useState([]);
  const [selectedBorderIds, setSelectedBorderIds] = useState([]);
  const [borderLoadError, setBorderLoadError] = useState('');

  // Load the same public border library previously used automatically by the PDF
  // generator, so users can preview and choose the borders before exporting.
  useEffect(() => {
    let cancelled = false;
    const toDataUrl = (blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    const loadBorders = async () => {
      try {
        const base = process.env.PUBLIC_URL || '';
        const listResponse = await fetch(`${base}/borderImages.txt`);
        if (!listResponse.ok) throw new Error('Could not load borderImages.txt');
        const names = (await listResponse.text()).split(/\\r?\\n/).map(x => x.trim()).filter(Boolean);
        const options = await Promise.all(names.map(async (name, index) => {
          try {
            const response = await fetch(`${base}/images/${encodeURIComponent(name)}`);
            if (!response.ok) return null;
            const dataUrl = await toDataUrl(await response.blob());
            return { id: `existing-${index}-${name}`, name, dataUrl, source: 'existing' };
          } catch {
            return null;
          }
        }));
        const available = options.filter(Boolean);
        if (!cancelled) {
          setBorderOptions(available);
          setSelectedBorderIds(available.map(item => item.id));
          if (!available.length) setBorderLoadError('No existing border images could be loaded. You can upload custom borders below.');
        }
      } catch (error) {
        if (!cancelled) setBorderLoadError('Existing border library could not be loaded. You can still upload custom border images.');
        console.warn('Unable to load border library:', error);
      }
    };

    loadBorders();
    return () => { cancelled = true; };
  }, []);

  const handleCustomBorderUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const accepted = files.filter(file => /^image\/(png|jpeg|jpg|webp)$/i.test(file.type));
    if (!accepted.length) {
      setBorderLoadError('Please choose PNG, JPG, or WebP image files.');
      event.target.value = '';
      return;
    }
    try {
      const uploaded = await Promise.all(accepted.map(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const image = new Image();
          image.onload = () => {
            // Normalize uploaded formats (including WebP) to PNG for reliable
            // jsPDF embedding while preserving the original image dimensions.
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const context = canvas.getContext('2d');
            if (!context) {
              reject(new Error('Could not prepare the uploaded border image.'));
              return;
            }
            context.drawImage(image, 0, 0);
            resolve({
              id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`,
              name: file.name,
              dataUrl: canvas.toDataURL('image/png'),
              source: 'custom',
            });
          };
          image.onerror = () => reject(new Error(`Unable to decode ${file.name}`));
          image.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })));
      setBorderOptions(previous => [...previous, ...uploaded]);
      setSelectedBorderIds(previous => [...previous, ...uploaded.map(item => item.id)]);
      setBorderLoadError('');
    } catch (error) {
      console.error('Unable to read custom border images:', error);
      setBorderLoadError('Unable to read one or more selected images.');
    } finally {
      event.target.value = '';
    }
  };

  const toggleBorderSelection = (id) => {
    setSelectedBorderIds(previous =>
      previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id]
    );
  };




  const [labels, setLabels] = useState({
    name: 'Name',
    study: 'Study',
    admNo: 'AdmNo',
    school: 'School',
  });

  // Read the same two worksheets previously fetched from Google Sheets.
  // Keep the row-array structure expected by generatePDF unchanged.
  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setIsReadingExcel(true);

    try {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!['xlsx', 'xls'].includes(extension)) {
        throw new Error('Please upload an Excel file (.xlsx or .xls).');
      }

      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: 'array',
        cellDates: false,
      });

      const getWorksheetRows = (requestedName) => {
        const actualName = workbook.SheetNames.find(
          (name) => name.trim().toLowerCase() === requestedName.toLowerCase()
        );
        if (!actualName) {
          throw new Error(`The Excel file must contain a worksheet named "${requestedName}".`);
        }

        return XLSX.utils.sheet_to_json(workbook.Sheets[actualName], {
          header: 1,
          raw: false,
          defval: '',
          blankrows: false,
        }).map((row) => row.map((cell) =>
          cell === null || cell === undefined ? '' : String(cell).trim()
        ));
      };

      const rows1 = getWorksheetRows('Sheet1');
      const rows2 = getWorksheetRows('Sheet2');

      if (!rows1.length) throw new Error('Sheet1 is empty. Please check your Excel file.');
      if (!rows2.length) throw new Error('Sheet2 is empty. Please check your Excel file.');

      setSheet1Data(rows1);
      setSheet2Data(rows2);
      setExcelFileName(file.name);
      setTimeTaken(null);
    } catch (error) {
      console.error('Error reading Excel file:', error);
      setSheet1Data([]);
      setSheet2Data([]);
      setExcelFileName('');
      setUploadError(error.message || 'Unable to read the Excel file.');
    } finally {
      setIsReadingExcel(false);
      event.target.value = '';
    }
  };

  // ✅ Generate PDF
  const handleGeneratePDF = async (selectedMode) => {
  const t = await generatePDF({
    sheet1Data,
    sheet2Data,
    pageSize,
    orientation,
    labels,
    classLabel,
    fontStyles,
    labelColors,
    valueColors,
    lineColors,
    setGridCounts,
    setTotalGrids,
    mode: selectedMode,
    selectedBorderImages: borderOptions
      .filter(item => selectedBorderIds.includes(item.id))
      .map(item => item.dataUrl),
  });

  setTimeTaken(t);
};


  return (
  <div className="personalized-bulk-app">
    {/* ===== Header ===== */}
    <header className="header">
      <div className="header-content">
        <div className="brand">Notebook Labels PDF Generator Tool</div>
        <div className="pro-section"></div>
      </div>
    </header>

    {/* ===== Main Flex Layout ===== */}
    <div
      className="main-content"
      style={{
        display: 'flex',
        gap: '2rem',
        alignItems: 'flex-start',
        padding: '1rem 2rem',
      }}
    >
      {/* ===== LEFT SIDE ONLY (Right side removed) ===== */}
      <div
        className="left-panel"
        style={{
          flex: 1,
          background: '#fafaff',
          padding: '15px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <h4 style={{ color: '#2c3e50', marginBottom: '1px' }}>Customize Labels</h4>

        {/* ===== Label Customization Section ===== */}
        <div
          className="customize-labels"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {[
            { label: 'Name', key: 'name', options: ['Name', 'Student'] },
            { label: 'Study', key: 'study', options: ['Study', 'Class'] },
            { label: 'AdmNo', key: 'admNo', options: ['AdmNo', 'RollNo'] },
            { label: 'School', key: 'school', options: ['School', 'College'] },
          ].map(({ label, key, options }) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <label
                style={{
                  fontWeight: '600',
                  color: '#34495e',
                  width: '40%',
                }}
              >
                {label}
              </label>
              <select
                value={labels[key]}
                onChange={(e) =>
                  setLabels({ ...labels, [key]: e.target.value })
                }
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  outline: 'none',
                }}
              >
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {/* After Superscript */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
            }}
          >
            <label
              style={{
                fontWeight: '600',
                color: '#34495e',
                width: '40%',
              }}
            >
              After Superscript
            </label>
            <select
              value={classLabel}
              onChange={(e) => setClassLabel(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="Class">Class</option>
              <option value="Standard">Standard</option>
              <option value="Grade">Grade</option>
              <option value="">None</option>
            </select>
          </div>
        </div>

        {/* ===== Excel Upload Section ===== */}
        <div
          style={{
            padding: '14px',
            border: '1px dashed #94a3b8',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <label
            htmlFor="excel-workbook-upload"
            style={{
              display: 'block',
              fontWeight: '600',
              color: '#34495e',
              marginBottom: '8px',
            }}
          >
            Upload Excel Workbook
          </label>
          <input
            id="excel-workbook-upload"
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleExcelUpload}
            disabled={isReadingExcel}
            style={{ width: '100%', fontSize: '14px' }}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
            Excel file must contain two worksheets named Sheet1 and Sheet2.
          </div>
          {isReadingExcel && (
            <p style={{ color: '#2563eb', margin: '8px 0 0', fontSize: '13px' }}>
              Reading Excel workbook...
            </p>
          )}
          {excelFileName && !uploadError && !isReadingExcel && (
            <p style={{ color: '#15803d', margin: '8px 0 0', fontSize: '13px' }}>
              Loaded: {excelFileName} ({sheet1Data.length} Sheet1 rows, {sheet2Data.length} Sheet2 rows)
            </p>
          )}
          {uploadError && (
            <p role="alert" style={{ color: '#dc2626', margin: '8px 0 0', fontSize: '13px' }}>
              {uploadError}
            </p>
          )}
        </div>

        {/* ===== Border Image Selection ===== */}
        <section
          style={{
            padding: '14px',
            border: '1px solid #dbe3ee',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#34495e' }}>Choose Label Borders</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                Selected borders repeat in sequence across the generated PDF grids.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setSelectedBorderIds(borderOptions.map(item => item.id))}
                disabled={!borderOptions.length}
                style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'pointer' }}>
                Select All
              </button>
              <button type="button" onClick={() => setSelectedBorderIds([])}
                disabled={!selectedBorderIds.length}
                style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>

          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(125px, 1fr))', gap: '10px' }}>
            {borderOptions.map(item => {
              const selected = selectedBorderIds.includes(item.id);
              return (
                <label key={item.id} style={{
                  display: 'flex', flexDirection: 'column', gap: '6px', padding: '7px',
                  border: selected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  borderRadius: '8px', background: selected ? '#eff6ff' : '#fff',
                  cursor: 'pointer', minWidth: 0,
                }}>
                  <img src={item.dataUrl} alt={item.name}
                    style={{ width: '100%', height: '92px', objectFit: 'contain', background: '#f8fafc', borderRadius: '4px' }} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#334155', overflowWrap: 'anywhere' }}>
                    <input type="checkbox" checked={selected} onChange={() => toggleBorderSelection(item.id)} />
                    <span>{item.name}</span>
                  </span>
                  {item.source === 'custom' && <span style={{ fontSize: '10px', color: '#2563eb' }}>Custom upload</span>}
                </label>
              );
            })}
          </div>
          {!borderOptions.length && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '10px' }}>
              Existing border previews will appear here when the public border library is available.
            </div>
          )}
          <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
            <label htmlFor="custom-border-upload" style={{ display: 'block', fontWeight: 600, color: '#34495e', marginBottom: '7px' }}>
              Upload Custom Border Images
            </label>
            <input id="custom-border-upload" type="file" accept="image/png,image/jpeg,image/webp" multiple
              onChange={handleCustomBorderUpload} style={{ width: '100%', fontSize: '13px' }} />
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
              Upload one or more PNG, JPG, or WebP borders. Uploaded borders are previewed, selected automatically, and repeated in order in the PDF.
            </div>
          </div>
          {borderLoadError && <p role="status" style={{ color: '#b45309', fontSize: '12px', margin: '8px 0 0' }}>{borderLoadError}</p>}
          <div style={{ fontSize: '12px', color: selectedBorderIds.length ? '#15803d' : '#b91c1c', marginTop: '8px' }}>
            {selectedBorderIds.length} border image(s) selected
          </div>
        </section>

        {/* ===== Controls Section ===== */}
        <div
          className="controls"
          style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          <div>
            <label
              style={{
                fontWeight: '600',
                marginRight: '05px',
                marginBottom: '4px',
              }}
            >
              Page Size
            </label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
                marginRight: '20px',
              }}
            >
              <option value="acustom">C305×460</option>
              <option value="a1">A1</option>
              <option value="a2">A2</option>
              <option value="a3">A3</option>
              <option value="a4">A4</option>
            </select>

            <label
              style={{
                fontWeight: '600',
                marginRight: '05px',
                marginBottom: '4px',
              }}
            >
              Orientation
            </label>
            <select
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
              }}
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </select>
          </div>

          <div>
            <label style={{ fontWeight: '600', marginBottom: '4px' }}>
              Mode
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
              }}
            >
              <option value="all">All</option>
              <option value="semi">Semi</option>
            </select>
          </div>

          <button
            onClick={() => handleGeneratePDF(mode)}
            disabled={!sheet1Data.length || !sheet2Data.length || isReadingExcel}
            style={{
              background: (!sheet1Data.length || !sheet2Data.length || isReadingExcel) ? '#94a3b8' : '#2563eb',
              color: '#fff',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: (!sheet1Data.length || !sheet2Data.length || isReadingExcel) ? 'not-allowed' : 'pointer',
              marginTop: '10px',
            }}
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* No RIGHT PANEL */}
    </div>

    {/* ===== Footer ===== */}
    <footer className="footer">
      <p>© 2025 Tringe.in | Label Generate Tool</p>
    </footer>
  </div>
);
}
export default SheetToPDF;
